import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WmsSidebar from '../../components/WmsSidebar';
import WmsTopbar from '../../components/wms_topbar';
import { createWmsAlmacen } from '../../api.wms';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

const NuevaAlmacen = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nombre: '',
    codigo: '',
    direccion: '',
    ciudad: '',
    estado: '',
    pais: '',
    lat: null,
    lng: null,
  });
  const [lados, setLados] = useState([]); // Cada lado: { longitud: number, angulo: number }
  const [nuevoLado, setNuevoLado] = useState({ longitud: '', angulo: '' });
  const [zoom, setZoom] = useState(1);

  // Ejemplo: obtener el almacén asignado al usuario (ajusta según tu app)
  const warehouseObj = JSON.parse(localStorage.getItem('wms_warehouse'));
  const warehouseName = warehouseObj?.name || 'Sin almacén asignado';

  // Escalado automático para que la figura siempre sea visible
  const getAutoZoom = (points) => {
    if (points.length < 2) return 1;
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    const svgW = 400, svgH = 260;
    const polyW = maxX - minX;
    const polyH = maxY - minY;
    // Dejar margen de 20px a cada lado
    const scaleW = (svgW - 40) / (polyW || 1);
    const scaleH = (svgH - 40) / (polyH || 1);
    return Math.min(scaleW, scaleH, 3);
  };

  // Añadir lado
  const handleAddLado = (e) => {
    e.preventDefault();
    if (!nuevoLado.longitud || isNaN(Number(nuevoLado.longitud))) return;
    let angulo = Number(nuevoLado.angulo);
    if (lados.length === 0) angulo = 0; // El primer lado siempre horizontal
    setLados([...lados, { longitud: Number(nuevoLado.longitud), angulo }]);
    setNuevoLado({ longitud: '', angulo: '' });
  };

  // Calcular puntos del polígono y centrar en el SVG
  const getPolygonPointsRaw = () => {
    let points = [{ x: 0, y: 0 }]; // punto inicial en (0,0)
    let anguloAcumulado = 0;
    lados.forEach((lado, i) => {
      anguloAcumulado += (i === 0 ? 0 : lado.angulo);
      const rad = (Math.PI / 180) * anguloAcumulado;
      const prev = points[points.length - 1];
      const x = prev.x + lado.longitud * 30 * Math.cos(rad);
      const y = prev.y + lado.longitud * 30 * Math.sin(rad);
      points.push({ x, y });
    });
    // Si hay más de 2 lados, cerrar el polígono
    if (lados.length > 2) {
      points[points.length - 1] = points[0];
    }
    return points;
  };

  // Ajustar zoom automáticamente cuando cambian los lados
  React.useEffect(() => {
    const points = getPolygonPointsRaw();
    setZoom(getAutoZoom(points));
    // eslint-disable-next-line
  }, [lados.length]);

  const getPolygonPoints = () => {
    const points = getPolygonPointsRaw();
    // Aplicar zoom
    const zoomedPoints = points.map(p => ({ x: p.x * zoom, y: p.y * zoom }));
    // Centrado automático:
    // 1. Calcular bounding box
    const minX = Math.min(...zoomedPoints.map(p => p.x));
    const maxX = Math.max(...zoomedPoints.map(p => p.x));
    const minY = Math.min(...zoomedPoints.map(p => p.y));
    const maxY = Math.max(...zoomedPoints.map(p => p.y));
    const svgW = 400, svgH = 260;
    const offsetX = (svgW - (maxX - minX)) / 2 - minX;
    const offsetY = (svgH - (maxY - minY)) / 2 - minY;
    // 2. Aplicar offset a todos los puntos
    const centeredPoints = zoomedPoints.map(p => ({ x: p.x + offsetX, y: p.y + offsetY }));
    return centeredPoints;
  };

  // Renderizar cotas (longitud y ángulo)
  const renderCotas = (points) => {
    const cotas = [];
    // Cotas de longitud
    for (let i = 0; i < lados.length && i + 1 < points.length; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const mx = (p1.x + p2.x) / 2;
      const my = (p1.y + p2.y) / 2;
      cotas.push(
        <g key={"l"+i}>
          <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#60a5fa" strokeWidth={3} />
          <text x={mx} y={my - 10} fontSize={14} fill="#1e293b" textAnchor="middle" fontWeight="bold">{lados[i].longitud} m</text>
        </g>
      );
    }
    // Cotas de ángulo (en cada vértice, excepto el primero)
    for (let i = 1; i < lados.length && i < points.length - 1; i++) {
      const p = points[i];
      // Calcular posición para el texto del ángulo (desplazado hacia afuera del vértice)
      const prev = points[i - 1];
      const next = points[i + 1];
      // Vector anterior y siguiente
      const v1 = { x: prev.x - p.x, y: prev.y - p.y };
      const v2 = { x: next.x - p.x, y: next.y - p.y };
      // Ángulo entre los dos lados (en radianes)
      const dot = v1.x * v2.x + v1.y * v2.y;
      const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
      const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
      let angle = Math.acos(Math.max(-1, Math.min(1, dot / (mag1 * mag2))));
      angle = angle * 180 / Math.PI;
      // Posición del texto: alejarlo del vértice
      const offset = 22;
      const bisector = { x: v1.x + v2.x, y: v1.y + v2.y };
      const bisMag = Math.sqrt(bisector.x * bisector.x + bisector.y * bisector.y) || 1;
      const tx = p.x + (bisector.x / bisMag) * offset;
      const ty = p.y + (bisector.y / bisMag) * offset;
      cotas.push(
        <text key={"a"+i} x={tx} y={ty} fontSize={13} fill="#f59e42" textAnchor="middle" fontWeight="bold" stroke="#fff" strokeWidth={0.7}>
          {angle.toFixed(0)}°
        </text>
      );
    }
    return cotas;
  };

  const points = getPolygonPoints();
  const polygonPointsStr = points.map(p => `${p.x},${p.y}`).join(' ');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleLadoInput = (e) => {
    const { name, value } = e.target;
    setNuevoLado({ ...nuevoLado, [name]: value });
  };

  const handleResetLados = () => setLados([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const almacenPayload = {
        ...form,
        lados: lados.map(l => ({ ...l })),
        // propiedades_pasillos eliminado
      };
      await createWmsAlmacen(almacenPayload);
      alert('Almacén guardado correctamente');
      navigate('/wms/almacenes');
    } catch (error) {
      alert('Error al guardar el almacén: ' + (error?.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="flex h-screen min-h-screen">
      <WmsSidebar activeItem="almacenes" setActiveItem={() => {}} className="h-full min-h-screen flex-shrink-0" />
      <div className="flex-1 min-h-0 flex flex-col transition-all duration-300 bg-gray-100 p-6 overflow-hidden">
        <WmsTopbar warehouseName={warehouseName} />
        <button
          className="mb-4 px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-600"
          onClick={() => navigate('/wms/almacenes')}
        >
          ← Regresar a gestión de almacenes
        </button>
        <h1 className="text-2xl font-bold mb-4">Nuevo Almacén</h1>
        <div className="bg-white rounded shadow p-6 max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-8">
            {/* Columna izquierda: formulario */}
            <div className="flex-1 space-y-4 min-w-[260px]">
              <div>
                <label className="block font-semibold">Nombre</label>
                <input type="text" name="nombre" value={form.nombre} onChange={handleChange} required className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">Código</label>
                <input type="text" name="codigo" value={form.codigo} onChange={handleChange} required className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">Dirección</label>
                <input type="text" name="direccion" value={form.direccion} onChange={handleChange} required className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">Ciudad</label>
                <input type="text" name="ciudad" value={form.ciudad} onChange={handleChange} required className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">Estado</label>
                <input type="text" name="estado" value={form.estado} onChange={handleChange} required className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">País</label>
                <input type="text" name="pais" value={form.pais} onChange={handleChange} required className="w-full border rounded px-2 py-1" />
              </div>
              <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full md:w-auto">Guardar Almacén</button>
            </div>
            {/* Columna derecha: previsualización polígono */}
            <div className="flex-1 min-w-[320px] flex flex-col">
              <label className="block font-semibold mb-2">Previsualización de polígono</label>
              <div className="h-80 w-full rounded border overflow-hidden flex-1 bg-gray-50 flex flex-col items-center justify-center">
                <div className="flex gap-2 mb-2">
                  <button type="button" className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-lg font-bold" onClick={() => setZoom(z => Math.min(z + 0.1, 3))}>＋</button>
                  <button type="button" className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-lg font-bold" onClick={() => setZoom(z => Math.max(z - 0.1, 0.2))}>－</button>
                  <span className="text-xs text-gray-500 ml-2">Zoom: {Math.round(zoom * 100)}%</span>
                </div>
                <svg width={400} height={260} style={{ background: '#f8fafc', border: '1px solid #cbd5e1' }}>
                  {lados.length > 0 && (
                    <g>
                      <polyline points={polygonPointsStr} fill="#bae6fd55" stroke="#0284c7" strokeWidth={2} />
                      {renderCotas(points)}
                    </g>
                  )}
                </svg>
                {/* Tabla de lados agregados con edición y eliminación */}
                <div className="w-full mt-4">
                  <table className="min-w-full text-xs border rounded bg-white">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-2 py-1">#</th>
                        <th className="px-2 py-1">Longitud (m)</th>
                        <th className="px-2 py-1">Ángulo (°)</th>
                        <th className="px-2 py-1">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lados.map((lado, idx) => (
                        <tr key={idx}>
                          <td className="px-2 py-1 text-center">{idx + 1}</td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              value={lado.longitud}
                              min="0.1"
                              step="0.1"
                              className="border rounded px-1 py-0.5 w-16 text-center"
                              onChange={e => {
                                const nuevos = [...lados];
                                nuevos[idx].longitud = Number(e.target.value);
                                setLados(nuevos);
                              }}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              value={lado.angulo}
                              min="0"
                              max="359"
                              step="1"
                              className="border rounded px-1 py-0.5 w-12 text-center"
                              onChange={e => {
                                const nuevos = [...lados];
                                nuevos[idx].angulo = Number(e.target.value);
                                setLados(nuevos);
                              }}
                            />
                          </td>
                          <td className="px-2 py-1 text-center">
                            <button
                              type="button"
                              className="text-red-600 hover:underline text-xs"
                              onClick={() => {
                                const nuevos = lados.filter((_, i) => i !== idx);
                                setLados(nuevos);
                              }}
                            >Eliminar</button>
                          </td>
                        </tr>
                      ))}
                      {lados.length === 0 && (
                        <tr><td colSpan={4} className="text-center text-gray-400 py-2">Sin lados agregados</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Formulario para añadir lado */}
                <form className="flex gap-2 mt-4 items-end">
                  <div>
                    <label className="block text-xs font-semibold">Longitud (m)</label>
                    <input type="number" name="longitud" min="0.1" step="0.1" value={nuevoLado.longitud} onChange={handleLadoInput} className="border rounded px-2 py-1 w-24" required />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold">Ángulo (°)</label>
                    <input type="number" name="angulo" min="0" max="359" step="1" value={nuevoLado.angulo} onChange={handleLadoInput} className="border rounded px-2 py-1 w-20" placeholder="0=recto" />
                  </div>
                  <button type="button" className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700" onClick={handleAddLado}>Añadir lado</button>
                  <button type="button" className="ml-2 text-xs text-red-600 underline" onClick={handleResetLados}>Limpiar</button>
                </form>
                <div className="text-xs text-gray-500 mt-2">Añade lados y ángulos para formar el perímetro del almacén. Si hay más de 2 lados, se cierra automáticamente.</div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NuevaAlmacen;
